// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential. Unauthorized copying, modification,
// or distribution is strictly prohibited.

/**
 * Reading a BuildingConnected bid-leveling export.
 *
 * The file is a bid form transposed: one block of columns per bidder, laid
 * out for a person to read across. The blocks repeat on a fixed stride, each
 * carrying Unit / Qty / Unit Cost / Total Cost / Notes, and the rows beneath
 * are grouped into sections -- LINE ITEMS, OTHER COSTS, ALTERNATES,
 * INCLUSIONS, and prose sections after them.
 *
 * ── The thing worth knowing before trusting any of it ───────────────────────
 *
 * The export prints "Leveled Bid" and "Base Bid" near the top, per bidder.
 * On a real file both rows held the SAME number for every bidder, and that
 * number was the bidder's own submitted price -- not the leveled one. The
 * leveling arithmetic lives only in the grid.
 *
 * On the file this was written against, one bidder's header said $593,345
 * while their leveled column added to $1,207,766, because $614,421 of their
 * comparison was plugs the estimator typed in on their behalf. A tool that
 * imported the header figure as "the bid" would state that bidder's price as
 * less than half of what it is being compared at, and would be most wrong
 * exactly when leveling mattered most.
 *
 * So nothing here treats the printed total as authoritative. Every bidder's
 * column is summed from the rows, and the printed figure is carried alongside
 * as `statedTotal` so the difference can be shown rather than resolved.
 *
 * ── Highlighting ────────────────────────────────────────────────────────────
 *
 * Adjusted cells carry a fill (FFDDBB on this export) and usually, but not
 * always, a note saying "Plug..." or "Corrected...". Neither signal is
 * reliable alone -- one cell was filled with no note at all -- so a cell
 * counts as adjusted if EITHER says so. This is the one place where reading
 * presentation as data is unavoidable: the distinction between a bidder's
 * price and the estimator's plug exists nowhere else in the file.
 */

/** A worksheet reduced to what this parser needs. */
export interface SheetGrid {
  /** Raw values, [row][col]. Numbers stay numbers. */
  values: Array<Array<string | number | null>>;
  /** Background fill per cell as RRGGBB, or null. Same shape as `values`. */
  fills: Array<Array<string | null>>;
}

export interface BcCell {
  amount: number | null;
  /** The estimator changed or supplied this figure. */
  adjusted: boolean;
  note: string;
}

export interface BcRow {
  section: string;
  /** The group header this row sits under, when it sits under one. */
  group: string;
  label: string;
  isGroupHeader: boolean;
  /** Index-aligned with `bidders`. */
  cells: BcCell[];
}

export interface BcBidder {
  name: string;
  submittedBy: string;
  proposalRef: string;
  /** What the export PRINTED. Not to be trusted as the leveled total. */
  statedTotal: number | null;
}

export interface BcLevelingSheet {
  projectName: string;
  packageName: string;
  bidders: BcBidder[];
  rows: BcRow[];
  /** Anything the parser could not account for, in plain words. */
  warnings: string[];
}

/** Sections the export uses as top-level headings. */
const SECTIONS = [
  "LINE ITEMS", "OTHER COSTS", "ALTERNATES", "INCLUSIONS", "EXCLUSIONS",
  "NOTES", "GENERAL ACKNOWLEDGMENTS", "SCOPE-SPECIFIC INFORMATION",
] as const;

/** Only this section rolls into the comparison; the rest are priced separately. */
export const COMPARISON_SECTION = "LINE ITEMS";

/** Offsets within one bidder's block, from the column headed "Unit". */
const OFF = { unit: 0, qty: 1, unitCost: 2, total: 3, note: 5 };

/** Fills that mean "the estimator touched this". White and near-white do not. */
const NEUTRAL_FILLS = new Set(["FFFFFF", "FAFAFA", "FFFFFFFF", "000000", ""]);

const text = (v: unknown): string => (v == null ? "" : String(v).trim());
const numeric = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);

const isAdjustedFill = (fill: string | null): boolean =>
  !!fill && !NEUTRAL_FILLS.has(fill.toUpperCase());

const looksPlugged = (note: string): boolean => /\b(plug|corrected)\b/i.test(note);

/**
 * Find the grid inside the document.
 *
 * Anchored on the repeated column headers rather than on fixed addresses: the
 * preamble above them is a variable number of lines of project and contact
 * detail, and the number of bidders changes every package.
 */
function locate(grid: SheetGrid) {
  const { values } = grid;
  for (let r = 0; r < values.length; r++) {
    const row = values[r] || [];
    const starts: number[] = [], totals: number[] = [];
    for (let c = 0; c < row.length; c++) {
      const v = text(row[c]);
      if (v === "Unit") starts.push(c);
      if (v === "Total Cost") totals.push(c);
    }
    if (starts.length >= 1 && totals.length === starts.length && starts.length >= 1) {
      // Confirm the stride the header implies actually holds, rather than
      // trusting a stray "Unit" somewhere in the prose above.
      const ok = starts.every((s, i) => totals[i] - s === OFF.total);
      if (ok) return { headerRow: r, starts };
    }
  }
  return null;
}

/**
 * The row above the header carrying the bidders' names.
 *
 * Several rows up there have a value in every bidder block -- the names, the
 * "Submitted by" line under them, Leveled Bid, Base Bid, the proposal
 * reference -- so "first row where every block is filled" lands on whichever
 * happens to be nearest, which is not the names.
 *
 * The names are identified by what sits directly beneath them: a row of
 * "Submitted by ...". That is specific enough to be unambiguous, and when the
 * export stops saying it, the fallback is the topmost row of plain text,
 * which is where a name block would still be.
 */
function findNameRow(grid: SheetGrid, headerRow: number, starts: number[]): number {
  const allText = (r: number) => {
    const row = grid.values[r] || [];
    return starts.every(c => text(row[c]) !== "" && typeof row[c] !== "number");
  };
  for (let r = headerRow - 1; r >= 1; r--) {
    if (!allText(r)) continue;
    const below = grid.values[r + 1] || [];
    if (starts.some(c => /^submitted by/i.test(text(below[c])))) return r;
  }
  let topmost = -1;
  for (let r = headerRow - 1; r >= 0; r--) if (allText(r)) topmost = r;
  return topmost;
}

/** The first row whose column B equals `label`. */
function findLabelRow(grid: SheetGrid, label: string): number {
  for (let r = 0; r < grid.values.length; r++) {
    if (text((grid.values[r] || [])[1]) === label) return r;
  }
  return -1;
}

/**
 * A group header is written in capitals -- but that is presentation, so it is
 * only a proposal. The caller verifies it against the arithmetic.
 */
const looksLikeGroup = (label: string): boolean =>
  label.length > 1 && label === label.toUpperCase() && /[A-Z]/.test(label);

export function parseBcLeveling(grid: SheetGrid): BcLevelingSheet {
  const warnings: string[] = [];
  const found = locate(grid);
  if (!found) {
    return {
      projectName: "", packageName: "", bidders: [], rows: [],
      warnings: ["Could not find the bid grid. The export needs a header row with Unit / Qty / Unit Cost / Total Cost per bidder."],
    };
  }
  const { headerRow, starts } = found;
  const nameRow = findNameRow(grid, headerRow, starts);
  if (nameRow === -1) warnings.push("Could not find the bidder names above the grid.");

  const val = (r: number, c: number) => (grid.values[r] || [])[c];
  const fil = (r: number, c: number) => (grid.fills[r] || [])[c] ?? null;

  const statedRow = findLabelRow(grid, "Leveled Bid");
  const bidders: BcBidder[] = starts.map(c => ({
    name: nameRow === -1 ? "" : text(val(nameRow, c)),
    submittedBy: nameRow === -1 ? "" : text(val(nameRow + 1, c)).replace(/^Submitted by\s*/i, ""),
    proposalRef: text(val(headerRow - 2, c)),
    statedTotal: statedRow === -1 ? null : numeric(val(statedRow, c)),
  }));

  // The package name sits in column B on the bidder-name row; the project
  // name is the first non-empty line of the preamble above it.
  const packageName = nameRow === -1 ? "" : text(val(nameRow, 1));
  let projectName = "";
  for (let r = 0; r < (nameRow === -1 ? 0 : nameRow); r++) {
    const v = text(val(r, 1));
    if (v && !/^(Prepared by|Bid Package Lead|Project Location|Generated)/i.test(v)) { projectName = v; break; }
  }

  const rows: BcRow[] = [];
  let section = "", group = "";
  for (let r = headerRow + 1; r < grid.values.length; r++) {
    const label = text(val(r, 1));
    if (!label) continue;
    if ((SECTIONS as readonly string[]).includes(label)) { section = label; group = ""; continue; }

    const cells: BcCell[] = starts.map((c) => {
      const note = text(val(r, c + OFF.note));
      const fill = fil(r, c + OFF.total);
      return {
        amount: numeric(val(r, c + OFF.total)),
        // Either signal is enough. One adjusted cell on the reference file
        // carried a fill and no note at all.
        adjusted: isAdjustedFill(fill) || looksPlugged(note),
        note,
      };
    });
    const isGroupHeader = looksLikeGroup(label);
    if (isGroupHeader) group = label;
    rows.push({ section, group: isGroupHeader ? "" : group, label, isGroupHeader, cells });
  }

  // Only the comparison section: elsewhere a group header is a label with a
  // placeholder zero under it, not a subtotal, and checking those produces
  // noise that trains the reader to ignore real warnings.
  verifyGroups(rows.filter(r => r.section === COMPARISON_SECTION), bidders.length, warnings);
  return { projectName, packageName, bidders, rows, warnings };
}


/**
 * Check each group header against the rows beneath it.
 *
 * The capitals heuristic proposes; this disposes. A header whose figure does
 * not equal its children's is reported rather than quietly double counted or
 * quietly dropped -- it means the layout is not what this parser assumes, and
 * the user needs to know that before any money moves.
 */
function verifyGroups(rows: BcRow[], bidderCount: number, warnings: string[]) {
  for (let i = 0; i < rows.length; i++) {
    const head = rows[i];
    if (!head.isGroupHeader) continue;
    const kids: BcRow[] = [];
    for (let j = i + 1; j < rows.length; j++) {
      if (rows[j].isGroupHeader) break;
      kids.push(rows[j]);
    }
    if (!kids.length) continue;
    for (let b = 0; b < bidderCount; b++) {
      const stated = head.cells[b]?.amount;
      if (stated == null) continue;
      const summed = kids.reduce((a, k) => a + (k.cells[b]?.amount ?? 0), 0);
      if (Math.abs(summed - stated) > 0.5) {
        warnings.push(`"${head.label}" does not equal the rows under it for bidder ${b + 1} (printed ${Math.round(stated).toLocaleString()}, rows add to ${Math.round(summed).toLocaleString()})`);
      }
    }
  }
}

export interface BcBidderTotal {
  bidder: string;
  /** What the export printed near the top. */
  stated: number | null;
  /** Summed from the grid: what this bidder is actually being compared at. */
  leveled: number;
  /** The part of `leveled` the bidder themselves quoted. */
  own: number;
  /** The part the estimator supplied -- plugs and corrections. */
  plugged: number;
  /** leveled − stated, when the export printed one. */
  statedDelta: number | null;
}

/**
 * What each bidder actually comes to.
 *
 * Leaf rows only: group headers repeat their children and adding both would
 * double every figure in the package.
 */
export function bcBidderTotals(
  sheet: BcLevelingSheet, section: string = COMPARISON_SECTION,
): BcBidderTotal[] {
  const leaves = sheet.rows.filter(r => r.section === section && !r.isGroupHeader);
  return sheet.bidders.map((b, i) => {
    let leveled = 0, own = 0, plugged = 0;
    for (const row of leaves) {
      const c = row.cells[i];
      if (!c || c.amount == null) continue;
      leveled += c.amount;
      if (c.adjusted) plugged += c.amount; else own += c.amount;
    }
    return {
      bidder: b.name, stated: b.statedTotal, leveled, own, plugged,
      statedDelta: b.statedTotal == null ? null : leveled - b.statedTotal,
    };
  });
}

/** The sections this sheet actually carries priced rows in. */
export function bcSections(sheet: BcLevelingSheet): string[] {
  const out: string[] = [];
  for (const r of sheet.rows) {
    if (!out.includes(r.section) && r.cells.some(c => c.amount != null)) out.push(r.section);
  }
  return out;
}
